import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { MapSceneState } from './mapSceneState';
import { MAP_ANALYTICS_KEYS } from './analyticsOnce';
import * as PIXI from 'pixi.js';
import posthog from 'posthog-js';
import { MapObject } from '../../models/mapObject';
import {
  DECOR_ATLAS_JSON_PATH,
  isDecorInteractiveType,
} from '../../data/mapDecorAssets';
import { createPlotIcon, createSaleTag, MAP_OVERLAY_ALPHA } from './render/mapMarkers';
import { ensurePlotMarkerTexture } from './mapScene';
import { islandImagePath, loadMapAvatar, mapTexturePaths } from './mapPreload';
import useMapViewStore from '../../stores/useMapViewStore';
import { AVATAR_TOTAL, LIGHTHOUSE_GLOW_SIZE, avatarLetterTexture, avatarPhotoTexture, lighthouseGlowTexture } from './render/mapBadgeTextures';
import { asTickerBound, type MapMarkerContainer } from './render/mapPixiExtensions';

// ── Map-objects render effect ──
// Extracted verbatim from MapCanvas (decomposition step 04). The effect body is
// byte-identical to the original except that:
//  * MapCanvas-owned scene refs, derived data, state setters and the
//    module-level click-handler refs are received as parameters;
//  * module-level scene state (`decorAtlasSheet`) and the analytics did-track
//    flags (`didTrack*`) plus `mapEnterTs` are read/written through
//    getter/setter closures that stay owned by MapCanvas — the module-level
//    `let`s are shared with the scene-init / cleanup / incremental-add code,
//    so they are NOT moved here (rule 4a: no back-import into MapCanvas);
//  * `createRealEstateMarker` / `createMapObjectMarker` are declared later in
//    MapCanvas (TDZ) and shared with the incremental-add effect, so they are
//    passed in as arrow-wrappers;
//  * `createPlotIcon` (shared with MapCanvas's incremental-add effect) is
//    imported from the shared render/mapMarkers module and receives the plot
//    texture ref;
//  * the one `mapSceneSingleton` write is done through a small closure param.

type MarkerClickHandler = (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void;

export interface UseMapObjectsRenderParams {
  // Effect deps
  mapObjectsRenderKey: string;
  isPixiAppReady: boolean;
  /** Stable map scene bag (groups mutated in place). */
  sceneRef: MutableRefObject<MapSceneState>;
  highlight: MapObjectsRenderHighlightState;
  assets: MapObjectsRenderAssets;
  renderStatus: MapObjectsRenderStatus;
  clickHandlers: MapObjectsRenderClickHandlers;
  // Derived data
  selectedMapObject: MapObject | null;
  objectGroups: MapObjectsRenderObjectGroups;
  markerBuilders: MapObjectsRenderMarkerBuilders;
}

export interface MapObjectsRenderHighlightState {
  clearSingleton: () => void;
}

export interface MapObjectsRenderAssets {
  loadedTexturePaths: Set<string>;
  islandTypeToImage: Record<string, number>;
}

export interface MapObjectsRenderStatus {
  setLastRenderKey: (key: string | null) => void;
  setMarkersReady: (ready: boolean) => void;
}

export interface MapObjectsRenderClickHandlers {
  plots: MutableRefObject<MarkerClickHandler | null>;
  mapObjects: MutableRefObject<MarkerClickHandler | null>;
}

export interface MapObjectsRenderObjectGroups {
  plots: MapObject[];
  houses: MapObject[];
  cityhalls: MapObject[];
  shops: MapObject[];
  hellOfFames: MapObject[];
  decorObjects: MapObject[];
}

export interface MapObjectsRenderMarkerBuilders {
  realEstate: (obj: MapObject) => Promise<PIXI.Container | null>;
  mapObject: (obj: MapObject) => Promise<PIXI.Container | null>;
}

export function useMapObjectsRender({
  mapObjectsRenderKey,
  isPixiAppReady,
  sceneRef,
  highlight,
  assets,
  renderStatus,
  clickHandlers,
  selectedMapObject,
  objectGroups,
  markerBuilders,
}: UseMapObjectsRenderParams): void {
  useEffect(() => {
    const currentMapObjects = sceneRef.current.data.mapObjects;
    const currentIslands = sceneRef.current.data.islands;
    if (!sceneRef.current.layers.mapObjects || !currentMapObjects || !sceneRef.current.pixi.app || !isPixiAppReady) {
      return;
    }

    if (sceneRef.current.viewport.lastRenderKey === mapObjectsRenderKey) {
      // Markers already built for this exact data. On a fresh mount (e.g. returning
      // from a room interior where markers were kept alive in the singleton) the
      // markersReady state has reset to false — restore it so dependent overlays
      // (quests/sale tags/highlight) re-run without a full destroy+rebuild.
      if (sceneRef.current.markers.realEstate.size > 0 || sceneRef.current.markers.plots.size > 0 || sceneRef.current.markers.mapObjects.size > 0) {
        renderStatus.setMarkersReady(true);
        useMapViewStore.getState().setMapSceneBuilt(true);
      }
      return;
    }
    console.warn('[MapCanvas] FULL RE-RENDER triggered', {
      prevKey: sceneRef.current.viewport.lastRenderKey?.slice(0, 80),
      newKey: mapObjectsRenderKey.slice(0, 80),
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join(' | '),
    });
    renderStatus.setLastRenderKey(mapObjectsRenderKey);

    // Markers are about to be recreated — reset readiness signal
    renderStatus.setMarkersReady(false);
    useMapViewStore.getState().setMapSceneBuilt(false);

    const mapObjectsLayer = sceneRef.current.layers.mapObjects;
    const plotMarkers = sceneRef.current.markers.plots;
    const realEstateMarkers = sceneRef.current.markers.realEstate;
    const mapObjectMarkers = sceneRef.current.markers.mapObjects;
    let isMounted = true;

    console.warn('[MapCanvas] Main render effect: destroying all markers and rebuilding', {
      plots: plotMarkers.size, houses: realEstateMarkers.size, objects: mapObjectMarkers.size,
    });

    plotMarkers.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    plotMarkers.clear();

    realEstateMarkers.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    realEstateMarkers.clear();

    mapObjectMarkers.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    mapObjectMarkers.clear();

    const questMarkers = sceneRef.current.markers.quests;
    questMarkers.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    questMarkers.clear();

    sceneRef.current.markers.sales.forEach((m) => { if (m.parent) m.parent.removeChild(m); m.destroy(); });
    sceneRef.current.markers.sales.clear();

    sceneRef.current.markers.avatars.forEach((m) => { if (m.parent) m.parent.removeChild(m); m.destroy(); });
    sceneRef.current.markers.avatars.clear();

    // Clear existing island markers
    if (sceneRef.current.layers.island) {
      sceneRef.current.markers.islands.forEach((m) => { sceneRef.current.layers.island!.removeChild(m); m.destroy(); });
    }
    sceneRef.current.markers.islands.clear();

    // Textures are batch-preloaded in parallel below, so per-object marker
    // creation is just a synchronous cache hit — we can build more per frame.
    const CHUNK_SIZE = 24; // render this many objects, then yield a frame
    const yieldFrame = () => new Promise<void>(r => requestAnimationFrame(() => r()));

    const createAsyncMarkers = async (
      objects: MapObject[],
      createMarkerFn: (obj: MapObject) => Promise<PIXI.Container | null>,
      clickHandlerRef: MutableRefObject<
        ((obj: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void) | null
      >,
      markersMap: Map<number, PIXI.Container>,
      afterCreate?: (obj: MapObject, marker: PIXI.Container) => void,
    ) => {
      for (let i = 0; i < objects.length; i++) {
        if (!isMounted || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) return;

        // Yield to the browser every CHUNK_SIZE objects to avoid memory spikes
        if (i > 0 && i % CHUNK_SIZE === 0) {
          await yieldFrame();
        }

        const obj = objects[i];
        try {
          const marker = await createMarkerFn(obj);

          if (!marker) continue;

          if (!isMounted || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) {
            if (!marker.destroyed) {
              try {
                marker.destroy();
              } catch { /* destroy best-effort */ }
            }
            return;
          }

          marker.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
            clickHandlerRef.current?.(obj, marker, event);
          });

          mapObjectsLayer.addChild(marker);
          markersMap.set(obj.id, marker);
          afterCreate?.(obj, marker);
        } catch (error) {
          console.error(`[MapCanvas] Error creating marker for ${obj.type} id ${obj.id}:`, error);
        }
      }
    };

    // Plot markers wait for their texture. The scene can be "ready" while map
    // textures are still reloading after a previous unmount freed them, so building
    // synchronously here is what used to yield textureless (green-circle) plots.
    void ensurePlotMarkerTexture().then((plotMarkerTexture) => {
      for (const plot of objectGroups.plots) {
        if (!isMounted || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) break;

        const marker = createPlotIcon(plot, plotMarkerTexture);
        marker.visible = sceneRef.current.flags.showPlots;

        marker.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
          clickHandlers.plots.current?.(plot, marker, event);
        });

        sceneRef.current.layers.mapObjects.addChild(marker);
        plotMarkers.set(plot.id, marker);
      }
    }).catch((e) => {
      console.error('[MapCanvas] plot marker texture unavailable — plots not drawn:', e);
    });

    // Create markers
    (async () => {
      const islandsRendered = (async () => {
        // Islands first — they are the map. All their pictures are requested at
        // once (the splash usually preloaded them, mapPreload.ts), and the island
        // layer fills in while the houses are still being built below.
        const islandTextures = new Map<string, Promise<PIXI.Texture>>();
        for (const island of currentIslands) {
          const path = islandImagePath(island.type);
          if (islandTextures.has(path)) continue;
          const texture = PIXI.Assets.load<PIXI.Texture>(path);
          texture.catch(() => { /* reported per island below */ });
          islandTextures.set(path, texture);
        }
        for (let islandIdx = 0; islandIdx < currentIslands.length; islandIdx++) {
          const island = currentIslands[islandIdx];
          if (!isMounted || !sceneRef.current.layers.island || !sceneRef.current.pixi.app) break;
          if (islandIdx > 0 && islandIdx % CHUNK_SIZE === 0) await yieldFrame();
          try {
            const islandPath = islandImagePath(island.type);
            const texture = await islandTextures.get(islandPath)!;
            assets.loadedTexturePaths.add(islandPath);
            if (texture.source) {
              texture.source.autoGenerateMipmaps = true;
              texture.source.scaleMode = 'linear';
            }
            if (!isMounted || !sceneRef.current.layers.island) break;
            const container = new PIXI.Container();
            const sprite = new PIXI.Sprite(texture);
            sprite.anchor.set(0.5);
            if (island.display_width && island.display_height) {
              sprite.width = island.display_width;
              sprite.height = island.display_height;
            }
            if (island.data?.mirror_h) {
              sprite.scale.x = -Math.abs(sprite.scale.x);
            }
            if (island.data?.rotate_180) {
              sprite.scale.x = -Math.abs(sprite.scale.x);
            }
            container.addChild(sprite);
            container.position.set(island.x, island.y);
            container.cullable = true;
            (container as MapMarkerContainer).mapObjectData = island;

            // Взаємодія островів у режимі бога підключається в useEffect(syncIslandGodModeInteractions)
            container.eventMode = 'none';

            sceneRef.current.layers.island.addChild(container);
            sceneRef.current.markers.islands.set(island.id, container);
          } catch (error) {
            console.error(`[MapCanvas] Failed to load island texture id=${island.id}:`, error);
          }
        }
      })();

      // Avatar texture cache: deduplicate by photo_url so each photo loads only once
      const avatarTextureCache = new Map<string, Promise<PIXI.Texture | null>>();

      // Same cache entry as the splash's preload (mapPreload.ts).
      const loadAvatarTexture = (photoUrl: string): Promise<PIXI.Texture | null> => {
        if (!avatarTextureCache.has(photoUrl)) avatarTextureCache.set(photoUrl, loadMapAvatar(photoUrl));
        return avatarTextureCache.get(photoUrl)!;
      };

      const currentListingsByHouseId = sceneRef.current.data.listingsByHouseId;

      // Shared "for sale" tag over houses (green price).
      const buildSaleTag = (
        obj: MapObject,
        tagY: number,
        opts?: { label?: string; bgColor?: number; force?: boolean },
      ) => {
        if (!sceneRef.current.layers.mapObjects) return;
        const listing = currentListingsByHouseId.get(obj.id);
        if (!listing && !opts?.force) return;
        const saleContainer = createSaleTag(obj, tagY, {
          label: opts?.label,
          bgColor: opts?.bgColor,
          showPlots: sceneRef.current.flags.showPlots,
        });
        sceneRef.current.layers.mapObjects.addChild(saleContainer);
        sceneRef.current.markers.sales.set(obj.id, saleContainer);
      };

      // A soft, gently pulsing glow at the lighthouse lamp (top of the sprite).
      // Sale/price plaques under the lighthouse were removed — they only cluttered the map.
      const addLighthouseGlow = (marker: PIXI.Container) => {
        const app = sceneRef.current.pixi.app;
        if (!app) return;
        // A ready-blurred disc (render/mapBadgeTextures.ts) — a BlurFilter re-rendered it every frame.
        const glow = new PIXI.Sprite(lighthouseGlowTexture());
        glow.anchor.set(0.5);
        glow.width = LIGHTHOUSE_GLOW_SIZE;
        glow.height = LIGHTHOUSE_GLOW_SIZE;
        glow.position.set(0, -60); // lamp room near the top (sprite anchored at centre)
        glow.alpha = 0.35;
        glow.eventMode = 'none';
        marker.addChildAt(glow, 0); // behind the lighthouse sprite so light spills around it
        const cb = () => {
          // smooth fade in/out, ~3.3s period, subtle (0.18..0.6)
          const t = performance.now() / 1000;
          glow.alpha = 0.18 + 0.42 * (0.5 + 0.5 * Math.sin(t * 1.9));
        };
        app.ticker.add(cb);
        marker.once('destroyed', () => { try { app.ticker.remove(cb); } catch { /* noop */ } });
      };

      // ONLY the lighthouse among decor gets the lamp glow (it's the single
      // buyable/tradeable decor). All other decor (oasis, trees, …) get nothing.
      // Sale/price plaques under the lighthouse were removed — they only cluttered the map.
      const afterDecorCreated = (obj: MapObject, marker: PIXI.Container) => {
        if (obj.type !== 'decor_lighthouse') return;
        addLighthouseGlow(marker);
      };

      const afterHouseCreated = (house: MapObject, _houseMarker: PIXI.Container) => {
        if (!sceneRef.current.layers.mapObjects) return;

        // ── Sale price tag ──
        const listing = currentListingsByHouseId.get(house.id);
        buildSaleTag(house, house.y - 70);

        // ── Owner avatar ──
        const owner = house.owner;
        if (!owner || !house.owner_id) return;

        const hasSaleTag = !!listing;
        const yOffset = hasSaleTag ? house.y - 115 : house.y - 80;

        const container = new PIXI.Container();
        container.position.set(house.x, yOffset);
        container.alpha = MAP_OVERLAY_ALPHA;
        container.zIndex = house.y + 1001;
        container.eventMode = 'none';
        container.cullable = true;
        container.visible = sceneRef.current.flags.showPlots;

        const isVip = owner.vip_until ? new Date(owner.vip_until) > new Date() : false;

        // One sprite: the round badge (VIP ring included) is drawn once onto a
        // shared canvas texture (render/mapBadgeTextures.ts) — no stencil mask, no
        // Text. The first letter shows until the photo arrives.
        const letter = (owner.first_name?.[0] || owner.username?.[0] || '?').toUpperCase();
        const badge = new PIXI.Sprite(avatarLetterTexture(letter, isVip));
        badge.anchor.set(0.5);
        badge.width = AVATAR_TOTAL;
        badge.height = AVATAR_TOTAL;
        container.addChild(badge);

        sceneRef.current.layers.mapObjects.addChild(container);
        sceneRef.current.markers.avatars.set(house.id, container);

        // Load photo async (deduplicated + throttled by browser connection limit)
        if (owner.photo_url) {
          const photoUrl = owner.photo_url;
          loadAvatarTexture(photoUrl).then((texture) => {
            if (!texture || container.destroyed || badge.destroyed) return;
            const round = avatarPhotoTexture(photoUrl, texture, isVip);
            if (round) badge.texture = round;
          });
        }
      };

      // ── Batch-preload every distinct object texture in parallel ──────────
      // Without this, the createAsyncMarkers loops below load textures one at a
      // time (a network fetch + decode per object, yielding a frame every
      // CHUNK_SIZE), so a map with many objects renders visibly slowly on a cold
      // load. Warming the PIXI.Assets cache up-front collapses all of those into
      // a single parallel batch; each per-object load then resolves instantly
      // from cache. Distinct paths mirror the logic in createRealEstateMarker /
      // createMapObjectMarker. allSettled + per-item catch so one missing/broken
      // asset can never block the rest.
      // The same paths the splash preloads (mapPreload.ts) — one source, so the
      // preload always lands in the cache entries used here. Static decor come
      // from the shared atlas (loaded once below).
      const objectTexturePaths = new Set(mapTexturePaths([
        ...objectGroups.houses, ...objectGroups.cityhalls, ...objectGroups.shops,
        ...objectGroups.hellOfFames, ...objectGroups.decorObjects,
      ]));
      const needsDecorAtlas = objectGroups.decorObjects.some((d) => !isDecorInteractiveType(d.type));
      // Load the static-decor spritesheet ONCE; hold the Spritesheet for the
      // decor branch of createMapObjectMarker. Tracked as a single path so
      // cleanup unloads one shared baseTexture instead of 15 individual ones.
      if (needsDecorAtlas && !sceneRef.current.assets.decorAtlasSheet) {
        try {
          void (sceneRef.current.assets.decorAtlasSheet = await PIXI.Assets.load(DECOR_ATLAS_JSON_PATH));
          assets.loadedTexturePaths.add(DECOR_ATLAS_JSON_PATH);
        } catch (err) {
          console.error('[MapCanvas] Failed to load decor atlas — decor will fall back to per-file load:', err);
          void (sceneRef.current.assets.decorAtlasSheet = null);
        }
        if (!isMounted || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) return;
      }
      if (objectTexturePaths.size > 0) {
        await Promise.allSettled(
          [...objectTexturePaths].map((p) => PIXI.Assets.load(p).catch(() => null))
        );
        // Tracked like the islands, so leaving the map frees them and coming back
        // (or a self-healing rebuild, see sceneHealth.ts) loads them fresh — never a
        // cached texture whose image the GPU/OS already dropped.
        for (const p of objectTexturePaths) assets.loadedTexturePaths.add(p);
        if (!isMounted || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) return;
      }

      await createAsyncMarkers(objectGroups.houses, markerBuilders.realEstate, clickHandlers.plots, realEstateMarkers, afterHouseCreated);

      // Zoom до щойно купленого будинку: див. окремий useEffect purchaseHighlightMapObjectId —
      // він спрацьовує навіть коли mapObjectsRenderKey не змінився (наприклад після «OK» у попапі).

      await createAsyncMarkers(objectGroups.cityhalls, markerBuilders.mapObject, clickHandlers.mapObjects, mapObjectMarkers);

      // Signal that plot + cityhall markers are ready (zoom effect can proceed)
      if (isMounted) renderStatus.setMarkersReady(true);

      await createAsyncMarkers(objectGroups.shops, markerBuilders.mapObject, clickHandlers.mapObjects, mapObjectMarkers);
      await createAsyncMarkers(objectGroups.hellOfFames, markerBuilders.mapObject, clickHandlers.mapObjects, mapObjectMarkers);
      await createAsyncMarkers(objectGroups.decorObjects, markerBuilders.mapObject, clickHandlers.mapObjects, mapObjectMarkers, afterDecorCreated);

      // ── Stage 2: map objects rendered ──────────────────────────────
      if (!sceneRef.current.analytics.once.fired(MAP_ANALYTICS_KEYS.objectsRendered) && isMounted) {
        sceneRef.current.analytics.once.markFired(MAP_ANALYTICS_KEYS.objectsRendered);
        const expected = objectGroups.houses.length + objectGroups.cityhalls.length + objectGroups.shops.length + objectGroups.hellOfFames.length + objectGroups.decorObjects.length;
        const actual = realEstateMarkers.size + mapObjectMarkers.size;
        try {
          posthog.capture('map_objects_rendered', {
            expected_count: expected,
            rendered_count: actual,
            success_rate: expected > 0 ? Math.round((actual / expected) * 100) : 100,
            load_duration_ms: sceneRef.current.analytics.mapEnterTs ? Math.round(performance.now() - sceneRef.current.analytics.mapEnterTs) : null,
          });
        } catch { /* never block game */ }
      }

      if (!isMounted || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) return;

      if (selectedMapObject) {
        let marker: PIXI.Container | undefined;

        if (selectedMapObject.type === 'plot') {
          marker = plotMarkers.get(selectedMapObject.id);
        } else if (selectedMapObject.type === 'house') {
          marker = realEstateMarkers.get(selectedMapObject.id);
        } else {
          marker = mapObjectMarkers.get(selectedMapObject.id);
        }

        if (!marker || !marker.parent || marker.destroyed) {
          if (sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
            const hl = asTickerBound(sceneRef.current.sprites.mapObjectHighlight);
            if (sceneRef.current.pixi.app && hl?.tickerFunction) {
              sceneRef.current.pixi.app.ticker.remove(hl.tickerFunction);
            }
            sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
            sceneRef.current.sprites.mapObjectHighlight.destroy();
            sceneRef.current.sprites.mapObjectHighlight = null;
            highlight.clearSingleton();
          }
        }
      }

      await islandsRendered;
      if (isMounted) useMapViewStore.getState().setMapSceneBuilt(true);

      // ── Stage 3: islands rendered ──────────────────────────────────
      if (!sceneRef.current.analytics.once.fired(MAP_ANALYTICS_KEYS.islandsRendered) && isMounted) {
        sceneRef.current.analytics.once.markFired(MAP_ANALYTICS_KEYS.islandsRendered);
        const renderedIslands = sceneRef.current.markers.islands.size;
        try {
          posthog.capture('map_islands_rendered', {
            expected_count: currentIslands.length,
            rendered_count: renderedIslands,
            success_rate: currentIslands.length > 0 ? Math.round((renderedIslands / currentIslands.length) * 100) : 100,
            load_duration_ms: sceneRef.current.analytics.mapEnterTs ? Math.round(performance.now() - sceneRef.current.analytics.mapEnterTs) : null,
          });
        } catch { /* never block game */ }
      }

      // ── Stage 4: fully loaded — start 10s stability timer ─────────
      if (!sceneRef.current.analytics.once.fired(MAP_ANALYTICS_KEYS.fullyLoaded) && isMounted) {
        window.setTimeout(() => {
          if (sceneRef.current.analytics.once.fired(MAP_ANALYTICS_KEYS.fullyLoaded)) return;
          sceneRef.current.analytics.once.markFired(MAP_ANALYTICS_KEYS.fullyLoaded);
          // Read pixel from canvas center — non-transparent = something rendered.
          let canvasOk = false;
          try {
            const canvas = sceneRef.current.pixi.app?.canvas as HTMLCanvasElement | undefined;
            if (canvas) {
              const ctx = canvas.getContext('2d', { willReadFrequently: false }) ?? canvas.getContext('webgl2') ?? canvas.getContext('webgl');
              if (ctx && 'getImageData' in ctx) {
                const px = (ctx as CanvasRenderingContext2D).getImageData(
                  Math.floor(canvas.width / 2), Math.floor(canvas.height / 2), 1, 1
                ).data;
                canvasOk = px[3] > 0; // alpha > 0 means something is drawn
              } else {
                // WebGL — if we got a context it's alive
                canvasOk = !!ctx;
              }
            }
          } catch { /* pixel read failed — report as false */ }
          try {
            posthog.capture('map_fully_loaded_10s', {
              canvas_has_content: canvasOk,
              islands_on_stage: sceneRef.current.markers.islands.size,
              objects_on_stage: sceneRef.current.markers.realEstate.size + sceneRef.current.markers.mapObjects.size,
            });
          } catch { /* never block game */ }
        }, 10_000);
      }
    })();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- render key + readiness; omit scene helpers thrash
  }, [mapObjectsRenderKey, isPixiAppReady]);
}
